/* eslint-disable @typescript-eslint/naming-convention */
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { JsonHttpService } from '../../shared/http/json-http.service';
import { Distribution } from '../../distribution/models';
import { ConfigurationKey } from '../models/configuration-key';
import { ConfigurationKeyUsage } from '../models/configuration-key-usage';
import * as dev from '../models/data-field-group';
import { DataFieldGroup } from '../../shared/datafields/models/datafield-group';
import { DataFieldType } from '../models/data-field-type';
import { DistributionObjectGroup } from '../models/document-type-group';
import { DocumentTypeDeveloper, IdentificationTypeQualifier, LightTrackingOptions } from '../models/document-type';
import { DocumentConfig } from '../models/document-config';
import { ExceptionCategory } from '../models/exception-category';
import { ExceptionType } from '../models/exception-type';
import { ExportContainer } from '../models/export-container';
import { FileNamingType } from '../models/filenamingtype';
import { Format } from '../models/format';
import { FormatType } from '../models/format-type';
import { ItineraryStep } from '../models/itinerary';
import { InitStep } from '../../distribution/models/init-step';
import { DocumentConfigItineraryStep, ItineraryStepDocumentConfigEntity } from '../models/itinerary-step-document-config-entity';
import { TrackingField } from '../models/tracking-field';
import { Variant } from '../models/variant';
import { VariantVersion } from '../models/variant-version';
import { TransportType, TransportTypeArtifactInfo } from '../../transport/models/transport-type';
import { SystemInitStep, SystemInitStepEntity } from '../../distribution/models/system-init-step';
import { DataFieldValue } from '../../shared/datafields/models';
import { IntegratedFtpProviderEntity } from '../models/integrated-ftp-provider';
import { SystemIntegratedFtpProviderEntity } from '../models/system-integrated-ftp-provider';
import { XmlPropertyPromotionFilter, XmlPropertyPromotionFilterEntity } from '../models/xml-property-promotion-filter';
import { XmlPropertyPromotionFieldEntity } from '../models/xml-property-promotion-field';
import { DocumentTypeClassifier } from '../models/document-type-classifier';
import { DocumentTypeClassifierResolverEntity } from '../models/document-type-classifier-resolver';
import { DownloaderService } from '../../shared/download/downloader.service';
import { formatDate } from '@angular/common';
import { StatusEventSubscription } from '../models/status-event-subscription';
import { StatusEventSubscriptionStatusTypeEntity } from '../models/status-event-subscription-status-type';
import { BatchingConfiguration } from '../models/batchconfiguration';
import { DocumentConfigOutBatcher } from '../models/document-config-out-batcher';
import { ScheduleItem, Trigger } from '../../shared/scheduling/models';
import { PartnerConfiguration } from 'src/app/partner/models/partner-configuration';
import { DistributionConfiguration } from 'src/app/distribution/models/distribution-configuration';
import { TrackingUiAction } from '../models/tracking-ui-action';
import { map } from 'rxjs/operators';
import { ContentType } from 'src/app/distribution/models/content-type';
import { SchedulerTaskHandler } from 'src/app/configuration/models/scheduler-task-objects';
import { ContextPropertyNamespace } from '../models/context-property-namespace';

@Injectable({
    providedIn: 'root'
})
export class DeveloperService {
    constructor(
        private httpService: JsonHttpService,
        private downloaderService: DownloaderService) { }

    clearCache() {
        return this.httpService.post('/api/developer/cache/clear-cache', null).toPromise();
    }

    getContentTypes(): Promise<ContentType[]> {
        return this.httpService.get('/api/developer/contenttype/get-contenttypes').toPromise();
    }

    getFormatTypes(): Promise<FormatType[]> {
        return this.httpService.get('/api/developer/formattype/get-formattypes').toPromise();
    }

    saveFormatType(item: FormatType): Promise<FormatType> {
        return this.httpService.post('/api/developer/formattype/save-formattype', item, null, false).toPromise();
    }

    deleteFormatType(id: number): Promise<void> {
        const options = { params: new HttpParams().set('formatTypeId', id.toString()) };
        return this.httpService.post('/api/developer/formattype/delete-formattype', null, options, false).toPromise();
    }

    getFormatTypeDataFieldGroups(initStepId: number, formatTypeId?: number) {
        const options = { params: new HttpParams().set('initStepId', initStepId.toString()) };

        if (formatTypeId) {
            options.params = options.params.set('formatTypeId', formatTypeId.toString());
        }

        return this.httpService.get('/api/developer/formattype/get-formattypes-datafield-groups', options).toPromise();
    }

    isFormatTypeDisassemblerConfigurationOveridden(formatTypeId: number) {
        const options = { params: new HttpParams().set('formatTypeId', formatTypeId.toString()) };
        return this.httpService.get('/api/developer/formattype/is-formattype-disassembler-configuration-overidden', options).toPromise();
    }

    getFormats(): Promise<Format[]> {
        return this.httpService.get('/api/developer/format/get-formats').toPromise();
    }

    saveFormat(format: Format, formatTypeDisassemblerDataFieldValues: DataFieldValue[], useGenericErrorHandling: boolean = true): Promise<Format> {
        const saveObject = { format, formatTypeDisassemblerDataFieldValues };
        return this.httpService.post('/api/developer/format/save-format', saveObject, null, useGenericErrorHandling).toPromise();
    }

    deleteFormat(id: number, useGenericErrorHandling: boolean = true): Promise<void> {
        const options = { params: new HttpParams().set('formatId', id.toString()) };
        return this.httpService.post('/api/developer/format/delete-format', null, options, useGenericErrorHandling).toPromise();
    }

    getFormatDataFieldGroups(formatTypeId: number, formatId?: number) {
        const options = { params: new HttpParams().set('formatTypeId', formatTypeId.toString()) };

        if (formatId) {
            options.params = options.params.set('formatId', formatId.toString());
        }

        return this.httpService.get('/api/developer/format/get-format-datafield-groups', options).toPromise();
    }

    getVariants(): Promise<Variant[]> {
        return this.httpService.get('/api/developer/variant/get-variants').toPromise();
    }

    saveVariant(item: Variant): Promise<Variant> {
        return this.httpService.post('/api/developer/variant/save-variant', item, null, false).toPromise();
    }

    deleteVariant(id: number): Promise<void> {
        const options = { params: new HttpParams().set('variantId', id.toString()) };
        return this.httpService.post('/api/developer/variant/delete-variant', null, options, false).toPromise();
    }

    getVariantVersions(): Promise<VariantVersion[]> {
        return this.httpService.get('/api/developer/variantVersion/get-variantversions').toPromise();
    }

    saveVariantVersion(item: VariantVersion): Promise<VariantVersion> {
        return this.httpService.post('/api/developer/variantVersion/save-variantversion', item, null, false).toPromise();
    }

    deleteVariantVersion(id: number): Promise<void> {
        const options = { params: new HttpParams().set('variantVersionId', id.toString()) };
        return this.httpService.post('/api/developer/variantversion/delete-variantversion', null, options, false).toPromise();
    }

    getDistributionObjectGroups(): Promise<DistributionObjectGroup[]> {
        return this.httpService.get('/api/developer/distributionobjectgroup/get-distributionobjectgroups').toPromise();
    }

    saveDistributionObjectGroup(item: DistributionObjectGroup): Promise<DistributionObjectGroup> {
        return this.httpService.post('/api/developer/distributionobjectgroup/save-distributionobjectgroup', item, null, false).toPromise();
    }

    deleteDistributionObjectGroup(id: number): Promise<void> {
        const options = { params: new HttpParams().set('distributionObjectGroupId', id.toString()) };
        return this.httpService.post('/api/developer/distributionobjectgroup/delete-distributionobjectgroup', null, options, false).toPromise();
    }

    getDocumentTypes(): Promise<DocumentTypeDeveloper[]> {
        return this.httpService.getTyped<DocumentTypeDeveloper[]>('/api/developer/documenttype/get-documenttypes').pipe(
            map(documentTypes => documentTypes.map(dt => {
                dt.LightTrackingOptions = new LightTrackingOptions(dt.LightTrackingOptions);
                return dt;
            }))
        ).toPromise();
    }

    saveDocumentType(item: DocumentTypeDeveloper): Promise<DocumentTypeDeveloper> {
        return this.httpService.post('/api/developer/documenttype/save-documenttype', item, null, false).toPromise();
    }

    deleteDocumentType(id: number): Promise<void> {
        const options = { params: new HttpParams().set('distributionObjectId', id.toString()) };
        return this.httpService.post('/api/developer/documenttype/delete-documenttype', null, options, false).toPromise();
    }

    allowDeleteDocumentType(id: number): Promise<boolean> {
        return this.httpService.post('/api/developer/documenttype/allow-delete-documenttype', id).toPromise();
    }

    saveIdentificationTypeQualifier(item: IdentificationTypeQualifier): Promise<IdentificationTypeQualifier> {
        return this.httpService.post('/api/developer/identificationtypequalifier/save-qualifier', item).toPromise();
    }

    deleteIdentificationTypeQualifier(id: number): Promise<void> {
        const options = { params: new HttpParams().set('identificationTypeQualifiersId', id.toString()) };
        return this.httpService.post('/api/developer/identificationtypequalifier/delete-qualifier', null, options).toPromise();
    }

    /* CONTEXT PROPERTIES */

    getContextProperties(): Promise<ContextPropertyNamespace[]> {
        return this.httpService.get('/api/developer/contextproperty/get-contextproperties').toPromise();
    }

    /* DOCUMENT CONFIG - START */

    getDocumentConfigOutBatchers(documentConfigOutId: number): Promise<DocumentConfigOutBatcher[]> {
        const options = { params: new HttpParams().set('documentConfigOutId', documentConfigOutId.toString()) };
        return this.httpService.get('api/developer/documentconfig/get-batchers', options).toPromise();
    }

    getAllDocumentConfigOutBatchers(): Promise<DocumentConfigOutBatcher[]> {
        return this.httpService.get('api/developer/documentconfig/get-all-batchers').toPromise();
    }

    getBatcherDataFieldGroups(artifactId: string, documentConfigOutBatchersId: number | null): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        if (documentConfigOutBatchersId) {
            options.params = options.params.set('documentConfigOutBatchersId', documentConfigOutBatchersId);
        }
        return this.httpService.get('/api/developer/documentconfig/get-batcher-datafields', options).toPromise();
    }

    saveBatchers(batchers: DocumentConfigOutBatcher[]): Promise<void> {
        const saveObject = {
            DocumentConfigOutBatchers: batchers
        };
        return this.httpService.post('/api/developer/documentconfig/save-batchers', saveObject).toPromise();
    }

    async getDocumentConfigs(): Promise<DocumentConfig[]> {
        return this.httpService.get('/api/developer/documentconfig/get-documentconfigs').toPromise();
    }

    saveDocumentConfig(item: DocumentConfig, isCopy: boolean): Promise<DocumentConfig> {
        const options = { params: new HttpParams().set('isCopy', isCopy.toString()) };
        return this.httpService.post('/api/developer/documentconfig/save-documentconfig', item, options, false).toPromise();
    }

    getAllDocumentConfigItinerarySteps(): Promise<ItineraryStepDocumentConfigEntity[]> {
        return this.httpService.get('/api/distribution/documentconfig/get-all-documentconfig-itinerary-steps').toPromise();
    }

    getDocumentConfigItinerarySteps(documentConfigId: number): Promise<ItineraryStepDocumentConfigEntity[]> {
        const options = { params: new HttpParams().set('documentConfigId', documentConfigId.toString()) };
        return this.httpService.get('/api/distribution/documentconfig/get-documentconfig-itinerary-steps', options).toPromise();
    }

    getDocumentConfigItineraryStepDataFieldGroups(itineraryStepId: number, itineraryStepDocumentConfigEntityId?: number): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('itineraryStepId', itineraryStepId.toString()) };
        if (itineraryStepDocumentConfigEntityId) {
            options.params = options.params.set('itineraryStepDocumentConfigEntityId', itineraryStepDocumentConfigEntityId?.toString());
        }
        return this.httpService.get('/api/distribution/documentconfig/get-documentconfig-itinerary-step-configuration', options).toPromise();
    }

    saveDocumentConfigItineraryStepDataFieldGroups(documentConfigItineraryStep: DocumentConfigItineraryStep[], documentConfigId: number): Promise<DocumentConfigItineraryStep[]> {
        const options = { params: new HttpParams().set('documentConfigId', documentConfigId.toString()) };
        return this.httpService.post('/api/distribution/documentconfig/save-documentconfig-itinerary-step-configuration', documentConfigItineraryStep, options).toPromise();
    }

    deleteDocumentConfig(documentConfigId: number): Promise<DocumentConfig> {
        const options = { params: new HttpParams().set('documentConfigId', documentConfigId.toString()) };
        return this.httpService.post('/api/developer/documentconfig/delete-documentconfig', null, options, false).toPromise();
    }

    getDistributionsEffectedByItinerarySteps(documentConfigItineraryStep: DocumentConfigItineraryStep[]): Promise<Distribution[]> {
        return this.httpService.post('/api/distribution/documentconfig/get-distributions-effected-by-itinerary-steps', documentConfigItineraryStep).toPromise();
    }

    getDistributionsBlockingChange(documentConfigItineraryStep: DocumentConfigItineraryStep[]): Promise<Distribution[]> {
        return this.httpService.post('/api/distribution/documentconfig/get-distributions-blocking-change', documentConfigItineraryStep).toPromise();
    }

    /* DOCUMENT CONFIG - END */

    saveItineraryStep(item: ItineraryStep): Promise<ItineraryStep> {
        return this.httpService.post('/api/developer/itinerarystep/save-itinerarystep', item, null, false).toPromise();
    }

    deleteItineraryStep(id: number): Promise<void> {
        const options = { params: new HttpParams().set('itineraryStepId', id.toString()) };
        return this.httpService.post('/api/developer/itinerarystep/delete-itinerarystep', null, options, false).toPromise();
    }

    getItinerarySteps(): Promise<ItineraryStep[]> {
        return this.httpService.get('/api/developer/itinerarystep/get-itinerarysteps').toPromise();
    }

    getItineraryStepKey(artifactId: string): Promise<string> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.httpService.get('/api/developer/itinerarystep/get-itinerarystep-key', options).toPromise();
    }

    getInitializeSteps(): Promise<InitStep[]> {
        return this.httpService.get('/api/distribution/initialize/get-init-steps').toPromise();
    }

    getFileNamingTypes(): Promise<FileNamingType[]> {
        return this.httpService.get('/api/developer/filenamingtype/get-filenamingtypes').toPromise();
    }

    saveFileNamingType(item: FileNamingType): Promise<FileNamingType> {
        return this.httpService.post('/api/developer/filenamingtype/save-filenamingtype', item, null, false).toPromise();
    }

    deleteFileNamingType(id: number): Promise<void> {
        const options = { params: new HttpParams().set('fileNamingTypeId', id.toString()) };
        return this.httpService.post('/api/developer/filenamingtype/delete-filenamingtype', null, options, false).toPromise();
    }

    getExceptionCategories(): Promise<ExceptionCategory[]> {
        return this.httpService.get('/api/developer/exceptioncategory/get-exceptioncategories').toPromise();
    }

    getExceptionTypes(): Promise<ExceptionType[]> {
        return this.httpService.get('/api/developer/exceptiontype/get-exceptiontypes').toPromise();
    }

    saveExceptionType(item: ExceptionType): Promise<ExceptionType> {
        return this.httpService.post('/api/developer/exceptiontype/save-exceptiontype', item, null, false).toPromise();
    }

    deleteExceptionType(id: number): Promise<void> {
        const options = { params: new HttpParams().set('exceptionTypeId', id.toString()) };
        return this.httpService.post('/api/developer/exceptiontype/delete-exceptiontype', null, options, false).toPromise();
    }

    generateErrorCode(): Promise<string> {
        return this.httpService.get('/api/developer/exceptiontype/generate-errorcode').toPromise();
    }

    saveExceptionCategory(item: ExceptionCategory): Promise<ExceptionCategory> {
        return this.httpService.post('/api/developer/exceptioncategory/save-exceptioncategory', item).toPromise();
    }

    getTrackingFields(): Promise<TrackingField[]> {
        return this.httpService.get('/api/developer/trackingfield/get-trackingfields').toPromise();
    }

    saveTrackingField(item: TrackingField): Promise<TrackingField> {
        return this.httpService.post('/api/developer/trackingfield/save-trackingfield', item).toPromise();
    }

    deleteTrackingField(id: number, useGenericErrorHandling: (err: any, caught: Observable<any>) => boolean = _ => true): Promise<void> {
        const options = { params: new HttpParams().set('trackingFieldId', id.toString()) };
        return this.httpService.postWithErrorEval('/api/developer/trackingfield/delete-trackingfield', null, options, useGenericErrorHandling).toPromise();
    }

    getConfigurationKeys(): Promise<ConfigurationKey[]> {
        return this.httpService.get('/api/developer/configurationkey/get-configurationkeys').toPromise();
    }

    getPartnerConfigurationsByKey(configurationKeyId: number): Promise<PartnerConfiguration[]> {
        const options = { params: new HttpParams().set('configurationKeyId', configurationKeyId.toString()) };
        return this.httpService.get('/api/developer/partnerconfiguration/get-by-configurationkey', options, false).toPromise();
    }

    getDistributionConfigurationsByKey(configurationKeyId: number): Promise<DistributionConfiguration[]> {
        const options = { params: new HttpParams().set('configurationKeyId', configurationKeyId.toString()) };
        return this.httpService.get('/api/developer/distributionconfiguration/get-by-configurationkey', options, false).toPromise();
    }

    saveConfigurationKey(item: ConfigurationKey): Promise<ConfigurationKey> {
        return this.httpService.post('/api/developer/configurationkey/save-configurationkey', item).toPromise();
    }

    deleteConfigurationKey(id: number): Promise<void> {
        const options = { params: new HttpParams().set('configurationKeyId', id.toString()) };
        return this.httpService.post('/api/developer/configurationkey/delete-configurationkey', null, options, false).toPromise();
    }

    getConfigurationKeyUsages(): Promise<ConfigurationKeyUsage[]> {
        return this.httpService.get('/api/developer/configurationkeyusage/get-configurationkeyusages').toPromise();
    }

    getDataFieldGroups(): Promise<dev.DataFieldGroup[]> {
        return this.httpService.get('/api/developer/displaydatafield/get-displaydatafieldgroups').toPromise();
    }

    saveDataFieldGroup(item: dev.DataFieldGroup): Promise<dev.DataFieldGroup> {
        return this.httpService.post('/api/developer/displaydatafield/save-displaydatafieldgroup', item).toPromise();
    }

    deleteDataFieldGroup(id: number, useGenericErrorHandling: (err: any, caught: Observable<any>) => boolean = _ => true): Promise<void> {
        const options = { params: new HttpParams().set('displayDataFieldGroupId', id.toString()) };
        return this.httpService.postWithErrorEval('/api/developer/displaydatafield/delete-displaydatafieldgroup', null, options, useGenericErrorHandling).toPromise();
    }

    getDataFieldTypes(): Promise<DataFieldType[]> {
        return this.httpService.get('/api/developer/displaydatafield/get-displaydatafieldtypes').toPromise();
    }

    getAvailableTrackingFieldDataTypes(): Promise<DataFieldType[]> {
        return this.httpService.get('/api/developer/displaydatafield/get-availabletrackingfielddatatypes').toPromise();
    }

    async export(item: ExportContainer, includeArtifacts: boolean): Promise<any> {
        const filename = `Export_${formatDate(new Date(), 'yyyyMMdd_HHmmss', 'en')}`;
        const options = { params: new HttpParams().set('filename', filename) };
        options.params = options.params.set('includeArtifacts', includeArtifacts.toString());
        const token = await this.httpService.post('/api/developer/export/export', item, options, true).toPromise();
        return this.downloaderService.downloadFile(token);
    }

    isSystemItineraryStep(itineraryStep: ItineraryStep): boolean {
        const systemItinerarySteps = ['TransformToCdm', 'TrackCdm', 'TransformFromCdm', 'Assemble', 'Send', 'Batch'];
        return systemItinerarySteps.includes(itineraryStep.Name);
    }

    /** Transport Types */

    saveTransportType(transportType: TransportType) {
        return this.httpService.post('/api/developer/transport-types/save-transport-type', transportType, null, false).toPromise();
    }

    deleteTransportType(id: number): Promise<void> {
        const options = { params: new HttpParams().set('id', id.toString()) };
        return this.httpService.post('/api/developer/transport-types/delete-transport-type', null, options, false).toPromise();
    }

    getTransportTypeInfo(artifactId: string): Promise<TransportTypeArtifactInfo> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        return this.httpService.get('/api/developer/transport-types/get-transport-type-key', options).toPromise();
    }

    /** System init-steps */

    getSystemInitStepDataFieldGroups(initStepId: number, systemInitStepId?: number): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('initStepId', initStepId) };
        if (systemInitStepId) {
            options.params = options.params.set('systemInitStepId', systemInitStepId.toString());
        }
        return this.httpService.get('/api/developer/system-init-steps/get-system-step-datafields', options).toPromise();
    }

    getSystemInitStep(type: SystemInitStep) {
        const options = { params: new HttpParams().set('type', type.toString()) };
        return this.httpService.get('/api/developer/system-init-steps/get-system-step', options).toPromise();
    }

    saveSystemInitstep(entity: SystemInitStepEntity, dataFieldValues: DataFieldValue[]) {
        const saveObject = {
            SystemInitStep: entity,
            Values: dataFieldValues
        };
        return this.httpService.post('/api/developer/system-init-steps/save-system-step', saveObject).toPromise();
    }

    deleteSystemInitStep(id: number) {
        const options = { params: new HttpParams().set('id', id.toString()) };
        return this.httpService.post('/api/developer/system-init-steps/delete-system-step', null, options).toPromise();
    }

    /** Integrated FTP Providers */

    getIntegratedFtpProviders() {
        return this.httpService.getTyped<IntegratedFtpProviderEntity[]>('/api/developer/integrated-ftp/get-integrated-ftp-providers').toPromise();
    }

    saveIntegratedFtpProviders(entity: IntegratedFtpProviderEntity) {
        return this.httpService.post('/api/developer/integrated-ftp/save-integrated-ftp-providers', entity).toPromise();
    }

    deleteIntegratedFtpProvider(id: number) {
        const options = { params: new HttpParams().set('id', id.toString()) };
        return this.httpService.post('/api/developer/integrated-ftp/delete-integrated-ftp-providers', null, options).toPromise();
    }

    /** System Integrated FTP Provider */

    getSystemIntegratedFtpProviderDataFieldGroups(integratedFtpProviderId: number, systemIntegratedFtpProviderId?: number): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('integratedFtpProviderId', integratedFtpProviderId) };
        if (systemIntegratedFtpProviderId) {
            options.params = options.params.set('systemIntegratedFtpProviderId', systemIntegratedFtpProviderId.toString());
        }
        return this.httpService.get('/api/developer/integrated-ftp/get-system-integrated-ftp-provider-datafields', options).toPromise();
    }

    getSystemIntegratedFtpProvider() {
        return this.httpService.getTyped<SystemIntegratedFtpProviderEntity>('/api/developer/integrated-ftp/get-system-integrated-ftp-provider').toPromise();
    }

    saveSystemIntegratedFtpProvider(entity: SystemIntegratedFtpProviderEntity, dataFieldValues: DataFieldValue[]) {
        const saveObject = {
            SystemIntegratedFtpProvider: entity,
            Values: dataFieldValues
        };
        return this.httpService.post('/api/developer/integrated-ftp/save-system-integrated-ftp-provider', saveObject).toPromise();
    }

    deleteSystemIntegratedFtpProvider(id: number) {
        const options = { params: new HttpParams().set('id', id.toString()) };
        return this.httpService.post('/api/developer/integrated-ftp/delete-system-integrated-ftp-provider', null, options).toPromise();
    }

    getXmlPropertyPromotionFilters(): Promise<XmlPropertyPromotionFilter[]> {
        return this.httpService.get('/api/developer/xml-property-promotion/get-filters-with-fields').toPromise();
    }

    saveXmlPropertyPromotionFilter(item: XmlPropertyPromotionFilterEntity): Promise<XmlPropertyPromotionFilterEntity> {
        return this.httpService.post('/api/developer/xml-property-promotion/save-filter', item, null, false).toPromise();
    }

    deleteXmlPropertyPromotionFilter(id: number): Promise<void> {
        const options = { params: new HttpParams().set('filterId', id.toString()) };
        return this.httpService.post('/api/developer/xml-property-promotion/delete-filter', null, options, false).toPromise();
    }

    saveXmlPropertyPromotionField(item: XmlPropertyPromotionFieldEntity): Promise<XmlPropertyPromotionFieldEntity> {
        return this.httpService.post('/api/developer/xml-property-promotion/save-field', item, null, false).toPromise();
    }

    deleteXmlPropertyPromotionField(id: number): Promise<void> {
        const options = { params: new HttpParams().set('fieldId', id.toString()) };
        return this.httpService.post('/api/developer/xml-property-promotion/delete-field', null, options, false).toPromise();
    }

    /** Document Type Classifiers */

    getDocumentTypeClassifiers(): Promise<DocumentTypeClassifier[]> {
        return this.httpService.get('/api/developer/documenttypeclassifier/get-documenttypeclassifiers').toPromise();
    }

    saveDocumentTypeClassifier(item: DocumentTypeClassifier, documentTypeClassifierResolverDataFieldValues: DataFieldValue[],
        controlMessageInResolverDataFieldValues: DataFieldValue[]): Promise<DocumentTypeClassifier> {
        const saveObject = {
            DocumentTypeClassifier: item,
            DocumentTypeClassifierResolverConfigurationValues: documentTypeClassifierResolverDataFieldValues,
            ControlMessageInResolverConfigurationValues: controlMessageInResolverDataFieldValues
        };
        return this.httpService.post('/api/developer/documenttypeclassifier/save-documenttypeclassifier', saveObject, null, false).toPromise();
    }

    deleteDocumentTypeClassifier(id: number): Promise<void> {
        const options = { params: new HttpParams().set('documentTypeClassifierId', id.toString()) };
        return this.httpService.post('/api/developer/documenttypeclassifier/delete-documenttypeclassifier', null, options, false).toPromise();
    }

    getDocumentTypeClassifierResolverDataFieldGroups(artifactId: string, documentTypeClassifierId: number | null): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        if (documentTypeClassifierId) {
            options.params = options.params.set('documentTypeClassifierId', documentTypeClassifierId);
        }
        return this.httpService.get('/api/developer/documenttypeclassifier/get-documenttypeclassifierresolver-datafields', options).toPromise();
    }

    getControlMessageInResolverDataFieldGroups(artifactId: string, documentTypeClassifierId: number | null): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        if (documentTypeClassifierId) {
            options.params = options.params.set('documentTypeClassifierId', documentTypeClassifierId);
        }
        return this.httpService.get('/api/developer/documenttypeclassifier/get-controlmessageinresolver-datafields', options).toPromise();
    }

    /** Document Type Classifier Resolvers */

    getDocumentTypeClassifierResolvers(): Promise<DocumentTypeClassifierResolverEntity[]> {
        return this.httpService.get('/api/developer/documenttypeclassifierresolver/get-documenttypeclassifierresolvers').toPromise();
    }

    saveDocumentTypeClassifierResolver(item: DocumentTypeClassifierResolverEntity): Promise<DocumentTypeClassifierResolverEntity> {
        return this.httpService.post('/api/developer/documenttypeclassifierresolver/save-documenttypeclassifierresolver', item).toPromise();
    }

    deleteDocumentTypeClassifierResolver(id: number, useGenericErrorHandling: (err: any, caught: Observable<any>) => boolean = _ => true): Promise<void> {
        const options = { params: new HttpParams().set('documentTypeClassifierResolverId', id.toString()) };
        return this.httpService.postWithErrorEval('/api/developer/documenttypeclassifierresolver/delete-documenttypeclassifierresolver', null, options, useGenericErrorHandling).toPromise();
    }

    /** Status Event Subscriptions */

    getStatusEventSubscriptions(): Observable<StatusEventSubscription[]> {
        const parseDate = (d: number | string | Date) => d ? (date => isNaN(date.getTime()) ? undefined : date)(new Date(d)) : undefined;
        return this.httpService.getTyped<StatusEventSubscription[]>('/api/developer/statuseventsubscription/get-statuseventsubscriptions').pipe(
            map(subscriptions => subscriptions.map(sub => ({ ...sub, CreatedDate: parseDate(sub.CreatedDate), UpdatedDate: parseDate(sub.UpdatedDate) })))
        );
    }

    getStatusEventSubscriptionFieldGroups(statusEventSubscriptionId: number | null, artifactId: string): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        if (statusEventSubscriptionId) {
            options.params = options.params.set('subscriptionId', !!statusEventSubscriptionId ? statusEventSubscriptionId : null);
        }
        return this.httpService.get('/api/developer/statuseventsubscription/get-statuseventhandlerdatafields', options).toPromise();
    }

    saveStatusEventSubscription(subscription: StatusEventSubscription, statusEventHandlerValues: DataFieldValue[]): Promise<StatusEventSubscription> {
        const parseDate = (d: number | string | Date) => d ? (date => isNaN(date.getTime()) ? undefined : date)(new Date(d)) : undefined;
        const saveObject = { subscription, statusEventHandlerValues };
        return this.httpService.postTyped<StatusEventSubscription>('/api/developer/statuseventsubscription/save-statuseventsubscription', saveObject).pipe(
            map(sub => ({ ...sub, CreatedDate: parseDate(sub.CreatedDate), UpdatedDate: parseDate(sub.UpdatedDate) }))
        ).toPromise();
    }

    deleteStatusEventSubscription(subscriptionId: number): Promise<boolean> {
        const options = { params: new HttpParams().set('subscriptionId', subscriptionId.toString()) };
        return this.httpService.delete('/api/developer/statuseventsubscription/delete-statuseventsubscription', options).toPromise();
    }

    /** Status Event Subscription Status Types */

    getStatusEventSubscriptionStatusTypes(): Promise<StatusEventSubscriptionStatusTypeEntity[]> {
        return this.httpService.get('/api/developer/statuseventsubscriptionstatustype/get-statuseventsubscriptionstatustypes').toPromise();
    }

    // Batching Configuration
    getBatchingConfigurations(): Promise<BatchingConfiguration[]> {
        return this.httpService.get('/api/developer/batchingconfigurations/get-batchingconfigurations').toPromise();
    }
    saveBatchingConfiguration(entity: BatchingConfiguration): Promise<BatchingConfiguration> {
        return this.httpService.post('/api/developer/batchingconfigurations/save-batchingconfiguration', entity).toPromise();
    }

    deleteBatchingConfiguration(entity: BatchingConfiguration): Promise<BatchingConfiguration> {
        // Soft-deletes by saving with "IsDeleted" flag set to true
        return this.httpService.post('/api/developer/batchingconfigurations/delete-batchingconfiguration', entity).toPromise();
    }


    // scheduling items
    getScheduleItems(): Promise<ScheduleItem[]> {
        return this.httpService.get('/api/developer/scheduling/get-scheduleitems').toPromise();
    }


    // scheduling triggers
    getScheduleTriggers(): Promise<Trigger[]> {
        return this.httpService.get('/api/developer/scheduling/get-scheduletriggers').toPromise();
    }

    getTrackingUiActions(): Promise<TrackingUiAction[]> {
        return this.httpService.get('/api/developer/tracking-ui-action/get-tracking-ui-actions').toPromise();
    }

    saveTrackingUiAction(item: TrackingUiAction, dataFieldValues: DataFieldValue[]): Promise<TrackingUiAction> {
        const saveObject = {
            TrackingUiActionEntity: item,
            ConfigurationValues: dataFieldValues
        };
        return this.httpService.post('/api/developer/tracking-ui-action/save-tracking-ui-action', saveObject).toPromise();
    }

    deleteTrackingUiAction(id: number): Promise<void> {
        const options = { params: new HttpParams().set('trackingUiActionId', id.toString()) };
        return this.httpService.post('/api/developer/tracking-ui-action/delete-tracking-ui-action', null, options, false).toPromise();
    }

    getTrackingUiActionDataFieldGroups(artifactId: string, trackingUiActionId?: number): Promise<DataFieldGroup[]> {
        const options = { params: new HttpParams().set('artifactId', artifactId) };
        if (trackingUiActionId) {
            options.params = options.params.set('trackingUiActionId', trackingUiActionId);
        }
        return this.httpService.get('/api/developer/tracking-ui-action/get-tracking-ui-action-datafields', options).toPromise();
    }

    // scheduler task handlers
    saveSchedulerTaskHandler(entity: SchedulerTaskHandler): Promise<SchedulerTaskHandler>{
        return this.httpService.post('/api/developer/scheduler-task-handler/save-scheduler-task-handler', entity).toPromise();
    }

    deleteSchedulerTaskHandler(id: number): Promise<void>{
        const options = { params: new HttpParams().set('schedulerTaskHandlerId', id.toString()) };
        return this.httpService.delete('/api/developer/scheduler-task-handler/delete-scheduler-task-handler', options).toPromise();
    }
}
